import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { GetProcessDraftResponse, GetProcessLayoutResponse } from '@bliks-io/contracts';
import { processDraftKeys, processLayoutKeys } from '../../query-keys';
import { applyDraftCache, optimisticDraftUpdate, restoreDraftCache } from './draftCacheHelpers';

export interface DraftTransactionArgs<T> {
  queryClient: QueryClient;
  processId: string;
  /** Stage the compound op's full end state into the draft cache in one shot. */
  optimistic: (prev: GetProcessDraftResponse) => GetProcessDraftResponse;
  /** Companion layout stage — avoids the `{0,0}` sentinel flash on new nodes. */
  optimisticLayout?: (prev: GetProcessLayoutResponse) => GetProcessLayoutResponse;
  /** Execute the network calls. Call persist-layer API functions directly. */
  persist: () => Promise<T>;
  /** Rewrite the optimistic cache with authoritative data from `persist`. */
  reconcile?: (prev: GetProcessDraftResponse, result: T) => GetProcessDraftResponse;
  /** Likewise for the layout cache. */
  reconcileLayout?: (prev: GetProcessLayoutResponse, result: T) => GetProcessLayoutResponse;
  /**
   * Query keys to invalidate on success. Defaults to draft detail + draft
   * layout — the settle backstop: `reconcile`'s authoritative output keeps
   * the UI instant, the refetch guarantees convergence with server truth.
   */
  invalidate?: ReadonlyArray<QueryKey>;
}

/**
 * Compound-operation primitive: stage optimistic cache once, send the
 * gesture's one request (a server verb, per the 2026-10-10 amendment of ADR
 * 0007), reconcile once. On failure, restores the pre-transaction snapshot
 * via `restoreDraftCache`'s version-guard. Call inside a `useDraftGesture`
 * run: on its own this is not in the mutation cache, so Publish's flush and
 * "Saving…" cannot see it.
 */
export async function runDraftTransaction<T>(args: DraftTransactionArgs<T>): Promise<T> {
  const draftKey = processDraftKeys.detail(args.processId);
  const layoutKey = processLayoutKeys.detail(args.processId, 'draft');
  const prevLayout = args.optimisticLayout
    ? args.queryClient.getQueryData<GetProcessLayoutResponse>(layoutKey)
    : undefined;
  // Layout before draft, at both stages: a node rendered before its position
  // is cached gets auto-placed and that placement is persisted over the real one.
  if (args.optimisticLayout && prevLayout) {
    args.queryClient.setQueryData<GetProcessLayoutResponse>(
      layoutKey,
      args.optimisticLayout(prevLayout)
    );
  }
  const ctx = await optimisticDraftUpdate(args.queryClient, args.processId, args.optimistic);
  try {
    const result = await args.persist();
    if (args.reconcileLayout) {
      args.queryClient.setQueryData<GetProcessLayoutResponse>(layoutKey, (prev) =>
        prev ? args.reconcileLayout!(prev, result) : prev
      );
    }
    if (args.reconcile) {
      applyDraftCache(args.queryClient, args.processId, (prev) => args.reconcile!(prev, result));
    }
    const keys = args.invalidate ?? [draftKey, layoutKey];
    await Promise.all(keys.map((key) => args.queryClient.invalidateQueries({ queryKey: key })));
    return result;
  } catch (err) {
    restoreDraftCache(args.queryClient, args.processId, ctx);
    if (args.optimisticLayout && prevLayout) {
      args.queryClient.setQueryData<GetProcessLayoutResponse>(layoutKey, prevLayout);
    }
    throw err;
  }
}
